import type { ReactNode } from 'react';
import { useEffect } from 'react';

import type { UserModel } from '@snowit/types/users';

import FirebaseSDKFactory from '@snowit/firebase/FirebaseSDKFactory';

import usePermissions from '../usePermissions';
import useRoles from '../useRoles';
import { useAuthStore } from './store';

import { jwtDecode } from 'jwt-decode';

const AuthProvider = ({ children }: { children: ReactNode }) => {
  const firebase = FirebaseSDKFactory();
  const { getPermissions } = usePermissions();
  const { getRoles } = useRoles();
  const setSession = useAuthStore((state) => state.setSession);
  const reset = useAuthStore((state) => state.reset);

  useEffect(() => {
    const unsubscribe = firebase.onAuthChange(async (user) => {
      if (!user) {
        reset();
        return;
      }

      const token = await user.getIdToken();
      const db = await firebase.doc<UserModel>({ collectionName: 'users', id: user.uid });
      const permissions = await getPermissions();
      const roles = await getRoles();
      setSession({
        user,
        logged: true,
        checked: true,
        token,
        jwt: jwtDecode(token),
        db,
        permissions,
        roles,
      });
    });

    return unsubscribe;
  }, []);

  return children;
};

export default AuthProvider;
